import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import type { PinFolder } from '../lib/types';
import { Icon, type IconName } from './Icon';
import { menuHeight, resolveMenuPlacement, type MenuPlacement } from '../lib/menuPlacement';

/** Menu width. Fixed, so the placement maths knows it without a measure pass. */
const MENU_WIDTH = 220;

/**
 * CLUB-676 — the pin collection's menus and dialogs, per the Figma section
 * "04 · Dialogs & menus".
 *
 * Kept out of PinFolderTree so the list itself stays about listing. Every one
 * of these is a leaf: it takes what it needs, reports one decision back, and
 * knows nothing about folders as a whole.
 */

// ── Menu ───────────────────────────────────────────────────────────────────

export type MenuItem = {
  id: string;
  label: string;
  icon: IconName;
  /**
   * Which band of the menu this belongs to. A divider is drawn wherever the
   * group changes — so a group whose every item was filtered out leaves no
   * stray rule behind, which a hardcoded separator entry would.
   *
   * The design groups all four menus the same way: the doing-things items, then
   * the map/file items, then the destructive one alone at the bottom.
   */
  group?: number;
  /** Error-toned. */
  destructive?: boolean;
  onSelect: () => void;
};

/**
 * A small anchored menu.
 *
 * Items are FILTERED by the caller, never disabled: the spec's note on the
 * panel menu — "admin-only actions are hidden, not disabled, when the member
 * cannot edit the ground" — is the rule for all four menus here. A greyed row
 * still tells a view-only member the action exists and invites them to work out
 * why it will not run.
 */
export function PinMenu({
  items,
  onClose,
  anchorRef,
  align = 'right',
}: {
  items: MenuItem[];
  onClose: () => void;
  /** The control this menu belongs to; it is measured to place the menu. */
  anchorRef: React.RefObject<HTMLElement | null>;
  align?: 'left' | 'right';
}) {
  const dividers = items.filter((it, i) => i > 0 && (it.group ?? 0) !== (items[i - 1].group ?? 0)).length;
  // Placed against the VIEWPORT, so a menu on the last row of the list opens
  // upwards instead of off the bottom of the window. The height is derived
  // from the item count rather than measured, so the menu is never painted in
  // the wrong place first and corrected a frame later.
  const [pos, setPos] = useState<MenuPlacement | null>(null);
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const place = () =>
      setPos(
        resolveMenuPlacement(
          anchor.getBoundingClientRect(),
          { width: MENU_WIDTH, height: menuHeight(items.length, dividers) },
          { width: window.innerWidth, height: window.innerHeight },
          align,
        ),
      );
    place();
    // Re-placed on scroll as well as resize: the list scrolls under a fixed
    // menu, and a menu left behind at its old coordinates is worse than one
    // that moves with its row.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, align, items.length, dividers]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Click-anywhere-else to dismiss, the idiom MapLayersPicker uses. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={onClose} />
      <div
        role="menu"
        style={{
          position: 'fixed',
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          zIndex: 41,
          width: MENU_WIDTH,
          maxHeight: pos?.maxHeight,
          overflowY: 'auto',
          padding: '6px 0',
          background: COLORS.surfaceContainerLowest,
          border: `1px solid ${COLORS.surfaceContainerHighest}`,
          borderRadius: 12,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          // Hidden until placed, so it never flashes at the fallback offset.
          visibility: pos ? 'visible' : 'hidden',
        }}
      >
        {items.map((item, i) => (
          <Fragment key={item.id}>
            {i > 0 && (item.group ?? 0) !== (items[i - 1].group ?? 0) && (
              <div
                aria-hidden="true"
                style={{ height: 1, margin: '6px 0', background: COLORS.surfaceContainerHighest }}
              />
            )}
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                onClose();
                item.onSelect();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                height: 40,
                padding: '0 14px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 500,
                color: item.destructive ? COLORS.error : COLORS.onSurface,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = item.destructive ? COLORS.errorContainer : COLORS.surfaceContainerHigh;
              }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <Icon name={item.icon} size={18} />
              <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
            </button>
          </Fragment>
        ))}
      </div>
    </>
  );
}

// ── Dialog shell ───────────────────────────────────────────────────────────

/**
 * The shared frame: backdrop, centred card, Escape to cancel, focus moved in.
 * Modelled on ConfirmModal, which could not be reused directly — these carry a
 * text field and a two-option radio choice, not a yes/no.
 */
function DialogShell({
  title,
  children,
  onCancel,
  footer,
  labelledBy,
}: {
  title: string;
  children: ReactNode;
  onCancel: () => void;
  footer: ReactNode;
  labelledBy: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // The first field, or failing that the card, so keyboard focus is never
    // left behind on whatever opened this.
    const field = cardRef.current?.querySelector<HTMLElement>('input, [data-autofocus]');
    (field ?? cardRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={onCancel}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 420,
          background: COLORS.surfaceContainerLowest,
          borderRadius: 14,
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 20px 0' }}>
          <h2
            id={labelledBy}
            style={{ margin: 0, fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500, color: COLORS.onSurface }}
          >
            {title}
          </h2>
        </div>
        <div style={{ padding: '12px 20px 0' }}>{children}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '20px' }}>{footer}</div>
      </div>
    </div>
  );
}

// ── New folder / Rename folder ─────────────────────────────────────────────

/**
 * One component for both, as the spec asks ("same component as New folder,
 * different title and CTA").
 *
 * `taken` is every OTHER folder's name. Create stays disabled until the name is
 * non-empty and unused — checked here rather than left to the write to reject,
 * because two folders with one name is not an error the list can render its way
 * out of.
 */
export function FolderNameDialog({
  mode,
  initialName = '',
  taken,
  onSubmit,
  onCancel,
}: {
  mode: 'create' | 'rename';
  initialName?: string;
  taken: readonly string[];
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  const titleId = useId();
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Rename opens with the current name SELECTED, so typing replaces it —
    // renaming is usually rewording, not appending.
    if (mode === 'rename') inputRef.current?.select();
  }, [mode]);

  const trimmed = name.trim();
  // Only a NON-EMPTY name can clash. Comparing the empty field against the
  // folders greeted an untouched dialog with "a folder already has that name",
  // because production data really does contain a folder saved with none.
  const clash = trimmed.length > 0 && taken.some((n) => n.trim().toLowerCase() === trimmed.toLowerCase());
  const valid = trimmed.length > 0 && !clash;

  const submit = () => {
    if (valid) onSubmit(trimmed);
  };

  return (
    <DialogShell
      labelledBy={titleId}
      title={mode === 'create' ? t('pinFolders.newFolder') : t('pinFolders.renameFolder')}
      onCancel={onCancel}
      footer={
        <>
          <button type="button" className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('pinFolders.cancel')}
          </button>
          <button type="button" className="wh-btn wh-btn-primary" disabled={!valid} onClick={submit}>
            {mode === 'create' ? t('pinFolders.create') : t('pinFolders.rename')}
          </button>
        </>
      }
    >
      {mode === 'create' && (
        <p style={{ margin: '0 0 14px', fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
          {t('pinFolders.newFolderBody')}
        </p>
      )}
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.onSurfaceVariant, marginBottom: 6 }}>
        {t('pinFolders.folderNameLabel')}
      </label>
      <input
        ref={inputRef}
        className="wh-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        aria-invalid={clash}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {/* Named while typing rather than on submit — the Create button going
          dead with no reason given is the state this avoids. */}
      <div style={{ minHeight: 18, marginTop: 6, fontSize: 12, color: COLORS.error }}>
        {clash ? t('pinFolders.folderNameTaken') : ''}
      </div>
    </DialogShell>
  );
}

// ── Delete folder ──────────────────────────────────────────────────────────

/**
 * The folder delete, with the choice the design makes explicit.
 *
 * NOTE — this diverges from what the API currently allows. CLUB-676's own
 * correction says the app permits deleting only an EMPTY folder
 * (`PinCollectionDetailBloc._onFolderDeleted`, "Verbatim legacy guard"), and
 * that the web API answers 422 for a folder that still holds pins. Neither arm
 * below is therefore implementable against the built backend yet; both run
 * against the prototype's mock state. See the note in the panel's docs.
 */
export function DeleteFolderDialog({
  folderName,
  pinCount,
  allowDeletePins,
  onConfirm,
  onCancel,
}: {
  folderName: string;
  pinCount: number;
  /**
   * Whether the cascade arm is offered at all.
   *
   * "Delete the pins too" IS a pin deletion, so it takes the same permission
   * one does. A host that withholds `onDeletePins` — the Map tab, where
   * destroying a pin for the whole team is deliberately not on offer — must not
   * get it back through a folder's delete dialog.
   */
  allowDeletePins: boolean;
  onConfirm: (opts: { deletePins: boolean }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  const titleId = useId();
  // Defaults to keeping them. The destructive arm is never the resting state
  // of a dialog opened by a single click on a kebab item.
  const [deletePins, setDeletePins] = useState(false);
  const empty = pinCount === 0;

  return (
    <DialogShell
      labelledBy={titleId}
      title={t('pinFolders.deleteFolderTitle', { name: folderName })}
      onCancel={onCancel}
      footer={
        <>
          <button type="button" className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('pinFolders.cancel')}
          </button>
          <button
            type="button"
            className={deletePins ? 'wh-btn wh-btn-danger' : 'wh-btn wh-btn-primary'}
            onClick={() => onConfirm({ deletePins })}
          >
            {t('pinFolders.deleteFolderConfirm')}
          </button>
        </>
      }
    >
      {empty ? (
        <p style={{ margin: 0, fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
          {t('pinFolders.deleteFolderEmptyBody')}
        </p>
      ) : !allowDeletePins ? (
        // No choice to make: the pins survive, and the sentence says where
        // they go rather than leaving the reader to find out.
        <p style={{ margin: 0, fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
          {t('pinFolders.deleteFolderKeepOnlyBody', { count: pinCount })}
        </p>
      ) : (
        <>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
            {t('pinFolders.deleteFolderBody', { count: pinCount })}
          </p>
          <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ChoiceRow
              selected={!deletePins}
              onSelect={() => setDeletePins(false)}
              title={t('pinFolders.deleteFolderKeep')}
              sub={t('pinFolders.deleteFolderKeepSub')}
            />
            <ChoiceRow
              selected={deletePins}
              onSelect={() => setDeletePins(true)}
              tone="destructive"
              title={t('pinFolders.deleteFolderCascade')}
              sub={t('pinFolders.deleteFolderCascadeSub', { count: pinCount })}
            />
          </div>
        </>
      )}
    </DialogShell>
  );
}

function ChoiceRow({
  selected,
  onSelect,
  title,
  sub,
  tone = 'primary',
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  sub: string;
  tone?: 'primary' | 'destructive';
}) {
  const accent = tone === 'destructive' ? COLORS.error : COLORS.primary;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%',
        padding: '12px 14px',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: 'pointer',
        borderRadius: 10,
        background: selected ? (tone === 'destructive' ? COLORS.errorContainer : COLORS.primaryContainer) : 'none',
        border: `1px solid ${selected ? accent : COLORS.outlineVariant}`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          marginTop: 2,
          width: 16,
          height: 16,
          flexShrink: 0,
          borderRadius: 999,
          border: `2px solid ${selected ? accent : COLORS.outline}`,
          background: selected
            ? `radial-gradient(circle, ${accent} 0 4px, transparent 4px)`
            : 'transparent',
        }}
      />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tone === 'destructive' ? COLORS.error : COLORS.onSurface }}>
          {title}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: COLORS.onSurfaceVariant, marginTop: 2, lineHeight: 1.45 }}>
          {sub}
        </span>
      </span>
    </button>
  );
}

// ── Delete pins ────────────────────────────────────────────────────────────

/**
 * Names up to three of the pins, then falls back to "and N others" — a count
 * alone cannot tell you whether the selection is the one you meant.
 */
export function DeletePinsDialog({
  names,
  onConfirm,
  onCancel,
}: {
  names: readonly string[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  const titleId = useId();
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;
  const list =
    rest > 0
      ? t('pinFolders.deletePinsListOverflow', { names: shown.join(', '), count: rest })
      : shown.length > 1
      ? t('pinFolders.deletePinsList', { names: shown.slice(0, -1).join(', '), last: shown[shown.length - 1] })
      : shown[0] ?? '';

  return (
    <DialogShell
      labelledBy={titleId}
      title={t('pinFolders.deletePinsTitle', { count: names.length })}
      onCancel={onCancel}
      footer={
        <>
          <button type="button" className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('pinFolders.cancel')}
          </button>
          <button type="button" className="wh-btn wh-btn-danger" data-autofocus onClick={onConfirm}>
            {t('pinFolders.delete')}
          </button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
        {t('pinFolders.deletePinsBody', { list })}
      </p>
    </DialogShell>
  );
}

// ── Move / copy pins to a folder ───────────────────────────────────────────

export function MovePinsDialog({
  mode,
  count,
  folders,
  folderCounts,
  currentFolderId,
  onConfirm,
  onCancel,
}: {
  mode: 'move' | 'copy';
  count: number;
  folders: readonly PinFolder[];
  /** Items per folder, for the subtitle under each row. */
  folderCounts: Record<string, number>;
  /** The folder the selection is already in; offered but marked. */
  currentFolderId?: string | null;
  onConfirm: (folderId: string | null) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  const titleId = useId();
  const [target, setTarget] = useState<string | null>(null);

  const rowStyle = (active: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: 'pointer',
    borderRadius: 8,
    background: active ? COLORS.primaryContainer : 'none',
    border: `1px solid ${active ? COLORS.primary : 'transparent'}`,
  });

  return (
    <DialogShell
      labelledBy={titleId}
      title={
        mode === 'move'
          ? t('pinFolders.movePinsTitle', { count })
          : t('pinFolders.copyPinsTitle', { count })
      }
      onCancel={onCancel}
      footer={
        <>
          <button type="button" className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('pinFolders.cancel')}
          </button>
          <button type="button" className="wh-btn wh-btn-primary" onClick={() => onConfirm(target)}>
            {mode === 'move' ? t('pinFolders.move') : t('pinFolders.copy')}
          </button>
        </>
      }
    >
      <div role="radiogroup" style={{ maxHeight: 280, overflowY: 'auto', margin: '0 -4px' }}>
        {/* The root is a destination like any other — this is how a pin comes
            OUT of a folder without being deleted. */}
        <button type="button" role="radio" aria-checked={target === null} onClick={() => setTarget(null)} style={rowStyle(target === null)}>
          <Icon name="folder_off" size={20} color={COLORS.outline} />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{t('pinFolders.ungrouped')}</span>
            <span style={{ display: 'block', fontSize: 12, color: COLORS.outline }}>{t('pinFolders.ungroupedSub')}</span>
          </span>
        </button>
        {folders.map((f) => {
          const n = folderCounts[f.id] ?? 0;
          const isCurrent = f.id === currentFolderId;
          return (
            <button
              key={f.id}
              type="button"
              role="radio"
              aria-checked={target === f.id}
              onClick={() => setTarget(f.id)}
              style={rowStyle(target === f.id)}
            >
              <Icon name="folder" size={20} color={COLORS.outline} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: f.name.trim() ? COLORS.onSurface : COLORS.outline,
                    fontStyle: f.name.trim() ? undefined : 'italic',
                  }}
                >
                  {f.name.trim() || t('pinFolders.unnamedFolder')}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: COLORS.outline }}>
                  {n === 0 ? t('pinFolders.folderEmptyShort') : t('pinFolders.folderCountShort', { count: n })}
                  {isCurrent ? ` · ${t('pinFolders.currentFolder')}` : ''}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </DialogShell>
  );
}
